




































































































































import { Component, Vue, Watch, Provide } from 'vue-property-decorator';
import infiniteScroll from 'vue-infinite-scroll';
import { Route } from 'node_modules/vue-router/types';
import { focus } from '@/directives';
import { IIssue, IProject, ISprint, IUser } from '@/lib/interfaces';
import { projectMinimize } from '@/lib/helpers/project.helper';
import { getAllIssues, getProject, updateIssuePosition, createSprint, createIssue } from '@/services/api.service';
import { deleteIssue } from '@/services/api.service';
import Project from './Project.vue';
import { mapState } from 'vuex';
import draggable from 'vuedraggable';
import { isTouch } from '@/lib/helpers/common.helper';
import { getCookie, setCookie } from '@/lib/helpers/common.helper';

interface ISprintWithIssues extends ISprint {
  showTab: boolean;
  issues: IIssue[];
}

/**
 * @example
 * <div id="drag-backlog">
 * <div id="drag-sprint-5badf88effadbf686fc48a12">
 */
interface IDragableEvent extends Event {
  /** Позиция в массиве, из которого перетащили */
  oldIndex: number;
  /** Позиция в массиве, в который перетащили */
  newIndex: number;
  /** Контейнер, в который перетащили */
  to: HTMLElement;
  /** Контейнер, из которого перетащили */
  from: HTMLElement;
}

@Component({
  directives: {
    focus,
    infiniteScroll,
  },
  components: {
    draggable,
  },
  sockets: {
    issueCreated(this: IssueList, issue: IIssue) {
      this.unshiftIssue(issue);
    },
    issueUpdated(this: IssueList, newIssue: IIssue) {
      const oldIssue = this.issues.find((i) => i._id === newIssue._id);
      if (oldIssue) {
        oldIssue.title = newIssue.title;
        oldIssue.type = newIssue.type;
      }
    },
    issueDeleted(this: IssueList, issue: IIssue) {
      this.deleteIssue(issue);
    },
    issueMoved(this: IssueList, event: IDragableEvent) {
      this.moveIssue(event);
    },
    sprintCreated(this: IssueList, sprint: ISprint) {
      this.project!.sprints!.unshift(sprint);
    },
    sprintStarted(this: IssueList, newSprint: ISprint) {
      const sprints = this.project!.sprints!;
      const foundSprint = sprints.find((s) => s._id === newSprint._id);
      if (foundSprint) {
        Vue.set(foundSprint, 'start_at', newSprint.start_at);
        Vue.set(foundSprint, 'end_at', newSprint.end_at);
      }
    },
  },
  computed: mapState(['hiddenSprintTabs']),
})
export default class IssueList extends Vue {
  public hiddenSprintTabs!: { string: boolean };
  private isTouch = isTouch();

  private issues: IIssue[] = [];
  private project: IProject = (this.$parent as Project).project!;
  private createIssue: boolean = false;
  private createSprint: boolean = false;
  private currentPage: number = 1;
  private sprintsCurrentPage: number = 1;
  private newIssue: IIssue = {
    title: '',
    _creator_id: '',
    deleted: false,
    project: projectMinimize(this.project),
    priority: 0,
    position: 0,
    code: 0,
    comments: [],
  };
  private newSprint: ISprint = {
    title: '',
  };
  private priorSort: boolean = getCookie('prior_sort') === 'true' || false;

  @Watch('priorSort')
  private onPriorSortChanged(val: boolean, oldVal: boolean) {
    setCookie('prior_sort', val.toString());
  }

  private get groupedIssues() {
    const issues = this.issues.reduce(
      (
        result: {
          backlog: IIssue[];
          backlog_hidden: IIssue[];
          sprints: ISprintWithIssues[];
          sprints_hidden: ISprintWithIssues[];
        },
        issue: IIssue,
      ): typeof result => {
        // Если у issue нет _sprint_id - то это бэклог
        if (!issue._sprint_id) {
          result.backlog_hidden.push(issue);
        } else {
          // Находим в массиве спринтов наш
          const existingSprint = result.sprints_hidden.find((sprint) => sprint._id === issue._sprint_id);
          if (existingSprint) {
            // Добавляем ему нас(issue)
            existingSprint.issues.push(issue);
          }
        }
        return result;
      },
      {
        backlog: [],
        backlog_hidden: [],
        // Массив всех спринтов проекта с пустым массивом issues
        sprints: [],
        sprints_hidden: this.project!.sprints!.map((sprint) =>
          Object.assign(
            {
              issues: [],
              showTab: this.hiddenSprintTabs[sprint._id!] === undefined,
            },
            sprint,
          ),
        ),
      },
    );
    let sortAlg: (a: IIssue, b: IIssue) => number;
    if (this.priorSort) {
      sortAlg = (a, b) => b.priority - a.priority;
    } else {
      sortAlg = (a, b) => a.position! - b.position!;
    }
    issues.backlog_hidden.sort(sortAlg);
    issues.sprints_hidden.forEach((sprint) => {
      sprint.issues.sort(sortAlg);
    });
    issues.backlog.push(...issues.backlog_hidden.splice(0, 5 * this.currentPage));
    issues.sprints.push(...issues.sprints_hidden.splice(0, 2 * this.sprintsCurrentPage));
    return issues;
  }

  private loadMoreBacklog() {
    this.currentPage++;
  }

  private loadMoreSprints() {
    this.sprintsCurrentPage++;
  }

  private async onIssueMoved(e: IDragableEvent) {
    const movedIssue = this.moveIssue(e);
    if (movedIssue) {
      await updateIssuePosition(movedIssue);
    }
  }

  private moveIssue(e: IDragableEvent): IIssue | null {
    // Массив откуда перетащили
    let oldArray: IIssue[] = [];
    // Массив куда перетащили
    let newArray: IIssue[] = [];
    const oldSprintId = e.from.id.split('-')[2];
    const newSprintId = e.to.id.split('-')[2];
    // Если включена сортировка по приоритету, то перетасивать в одной секции запрещено
    if (this.priorSort && oldSprintId === newSprintId) {
      return null;
    }
    // Определяем, какой массив старый массив, по его _sprint_id
    if (oldSprintId) {
      oldArray = this.groupedIssues.sprints.find((sprint) => sprint._id === oldSprintId)!.issues;
    } else {
      oldArray = this.groupedIssues.backlog;
    }
    // Аналогично ищем новый массив
    if (newSprintId) {
      newArray = this.groupedIssues.sprints.find((sprint) => sprint._id === newSprintId)!.issues;
    } else {
      newArray = this.groupedIssues.backlog;
    }
    // Issue, который перетащили
    const issue = oldArray[e.oldIndex];
    // Если меняем позицию в том же блоке
    /* tslint:disable-next-line */
    if (newSprintId == oldSprintId) {
      // Если новая позиция меньше старой позиции
      if (e.newIndex < e.oldIndex) {
        for (let i = e.newIndex; i < e.oldIndex; ++i) {
          oldArray[i].position! += 1;
        }
      }
      if (e.newIndex > e.oldIndex) {
        for (let i = e.oldIndex + 1; i <= e.newIndex; ++i) {
          oldArray[i].position! -= 1;
        }
      }
    } else {
      for (let i = e.newIndex; i < newArray.length; ++i) {
        newArray[i].position! += 1;
      }
      for (let i = e.oldIndex + 1; i < oldArray.length; ++i) {
        oldArray[i].position! -= 1;
      }
    }
    issue.position = e.newIndex;
    this.$set(issue, '_sprint_id', newSprintId || null);
    return issue;
  }

  private deleteIssue(issue: IIssue) {
    const index = this.issues.findIndex((i) => i._id === issue._id);
    this.issues.splice(index, 1);
  }

  private async doneIssue() {
    // this.newIssue.position = this.groupedIssues.backlog.length;
    const issue = await createIssue(this.newIssue);
    this.unshiftIssue(issue);
    this.cancelIssue();
  }

  private async destroyIssue(issue: IIssue) {
    const issueId = issue._id;
    if (issueId) {
      const result = await deleteIssue(issueId);
      this.deleteIssue(issue);
    }
  }

  private unshiftIssue(issue: IIssue) {
    this.issues.unshift(issue);
    for (let i = 1; i < this.groupedIssues.backlog.length; i++) {
      this.groupedIssues.backlog[i].position!++;
    }
  }

  private cancelIssue() {
    this.newIssue.title = '';
    this.createIssue = false;
  }

  private async doneSprint() {
    const sprint = await createSprint(this.newSprint, this.project!._id!);
    this.project!.sprints!.unshift(sprint);
    this.cancelSprint();
  }

  private cancelSprint() {
    this.newSprint.title = '';
    this.createSprint = false;
  }

  private async beforeRouteEnter(to: Route, from: Route, next: (vm?: any) => void) {
    const issues: IIssue[] = await getAllIssues({
      project: to.params.project_id,
      deleted: false,
    });
    next((vm: IssueList) => {
      vm.issues = issues;
    });
  }

  private onClickSprintToggleButton(sprint: ISprintWithIssues) {
    this.$store.commit('toggleSprintTab', sprint._id);
  }
}
